import React, { useCallback } from 'react';
import {
  MasonryEngineNodeAction,
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useIntl } from '@leagueplatform/locales';
import { Box } from '@leagueplatform/genesis-core';
import { Video, GDSVideo } from '@leagueplatform/genesis-core-alpha';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';

export interface MasonryVideoNodeProperties {
  url: string;
  thumbnail: string;
  altText: string;
  transcript?: string;
}

export type MasonryVideoNodeActions = {
  onPlay?: MasonryEngineNodeAction;
  onPause?: MasonryEngineNodeAction;
  onEnd?: MasonryEngineNodeAction;
};

export type MasonryVideoNode = MasonryEngineNode<
  'video',
  MasonryVideoNodeProperties,
  MasonryVideoNodeActions
>;

export type MasonryVideoRendererProps =
  MasonryEngineNodeRendererProps<MasonryVideoNode>;

export const MasonryVideoRenderer = ({
  url,
  thumbnail,
  altText,
  onPlay,
  onPause,
  onEnd,
  rootElementRef,
  className,
  engineClassName,
  transcript,
  locatorId,
  css,
}: MasonryVideoRendererProps) => {
  const invokeAction = useInvokeAction();

  const { formatMessage } = useIntl();

  const attachListenersOnReady: GDSVideo.OnReady = useCallback(
    (player: NonNullable<GDSVideo.PlayerRef>) => {
      player.on('play', () => {
        if (onPlay) {
          invokeAction({ ...onPlay });
        }
      });
      player.on('pause', () => {
        if (onPause) {
          invokeAction({ ...onPause });
        }
      });
      player.on('ended', () => {
        if (onEnd) {
          invokeAction({ ...onEnd });
        }
      });
    },
    [invokeAction, onEnd, onPause, onPlay],
  );

  // src and poster are valid videojs PlayerOptions passed through the spread props
  const videoProps = {
    src: url,
    poster: thumbnail,
  } as unknown as GDSVideo.PlayerOptions;

  return (
    <Box
      aria-label={altText}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      <Video
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...videoProps}
        onReady={attachListenersOnReady}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(transcript && {
          transcript: {
            content: transcript,
            heading: formatMessage({ id: 'VIDEO_TRANSCRIPT' }),
            buttonLabel: formatMessage({ id: 'VIEW_TRANSCRIPT' }),
          },
        })}
      />
    </Box>
  );
};
